import React from 'react';
import { useTranslation } from 'react-i18next';
import { RankingData } from '../types';
import { formatEFPower, truncateEns, convertDatabaseFormatToEmoji } from '../utils/dataProcessor';
import { ProfileTooltip } from 'ethereum-identity-kit';
import 'ethereum-identity-kit/css';
import { useMediaQuery } from "react-responsive"
import { MOBILE_BREAKPOINT, MAX_ENS_LENGTH_MOBILE, MAX_ENS_LENGTH_DESKTOP } from '../constants';

interface RankingTableProps {
  data: RankingData[];
}

const RankingTable: React.FC<RankingTableProps> = ({ data }) => {
  const { t } = useTranslation();
  const containsEmoji = (text: string): boolean => {
    const emojiRegex = /(\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])/;
    return emojiRegex.test(text);
  };

  const useIsDesktop = () =>
    useMediaQuery({ query: `(min-width: ${MOBILE_BREAKPOINT}px)` })
  const isDesktop = useIsDesktop()
    const maxLen = isDesktop ? MAX_ENS_LENGTH_DESKTOP : MAX_ENS_LENGTH_MOBILE
  
  return (
    <div className="overflow-x-auto -mx-4 sm:mx-0 rounded-lg shadow">
      <table className="min-w-full bg-white overflow-hidden table-fixed">
          <thead className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white md:h-20">
            <tr>
              <th className="py-3 px-3 md:px-4 text-left text-x md:text-xl font-semibold w-16 md:w-20">{t('table.rank')}</th>
              <th className="py-3 px-3 md:px-4 text-left text-x md:text-xl font-semibold w-20 hidden md:table-cell">{t('table.id')}</th>
              <th className="py-3 px-3 md:px-0 text-center text-x md:text-xl font-semibold md:w-1/3 lg:w-1/5">{t('table.ensName')}</th>
              <th className="py-3 px-3 md:px-4 text-right text-x md:text-xl font-semibold w-28 md:w-24 lg:w-32">{t('table.efpower')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data.map((item, index) => (
              <tr 
                key={item.listId} 
                className={`hover:bg-blue-50 transition-colors duration-150 ${
                  index < 3 ? 'bg-gradient-to-r from-yellow-50 to-transparent' : ''
                }`}
              >
                <td className="py-3 px-3 md:px-4">
                  <div className="flex items-center">
                    {index < 3 ? (
                      <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full mr-2 ${
                        index === 0 ? 'bg-yellow-500 shadow-lg shadow-yellow-200' : 
                        index === 1 ? 'bg-gray-300 shadow-md shadow-gray-200' : 
                        'bg-amber-600 shadow-md shadow-amber-200'
                      } text-white font-bold text-xs`}>
                        {item.Rank}
                      </span>
                    ) : (
                      <span className="w-6 h-6 inline-flex items-center justify-center text-gray-600">
                        {item.Rank}
                      </span>
                    )}
                  </div>
                </td>
                <td className="py-3 px-3 md:px-4 text-gray-700 hidden md:table-cell">{item.listId}</td>
                <td className="flex py-3 px-3 md:px-4 justify-center">
                  <div className="absolute items-center">
                    <ProfileTooltip
                      addressOrName={item.address}
                      horizontalOffset={-110}
                      showSocials
                      showBio
                      onProfileClick={() => { window.open(`https://efp.app/${item.address}`, "_blank") }}
                    >
                      <span className={`font-medium hover:text-blue-800 truncate overflow-hidden block w-full transition-colors duration-200 text-center ${index < 10 ? 'text-blue-700' : 'text-gray-700'}`}>
                        {truncateEns(convertDatabaseFormatToEmoji(item.ensName), maxLen)
                          .split(".eth")
                          .map((part, index) => (
                            <span
                              key={index}
                              className={
                                index === 1
                                  ? ""
                                  : containsEmoji(part)
                                    ? "font-['Apple_Color_Emoji','Segoe_UI_Emoji','Noto_Color_Emoji','sans-serif']"
                                    : "font-sans"
                              }
                            >
                              {index === 1 ? ".eth" : part}
                            </span>
                          ))}
                      </span>
                    </ProfileTooltip> 
                  </div>
                </td>
                <td className="py-3 px-3 md:px-4 text-right font-mono font-semibold whitespace-nowrap group relative">
                  <span className={`${index < 10 ? 'text-blue-700' : 'text-gray-700'}`}>
                    {formatEFPower(item.EFPower)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
    </div>
  );
};

export default React.memo(RankingTable); 